// src/App.jsx
import React, { useState, useEffect, useMemo, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import Dashboard from "./Dashboard";

// 👇 usa SOLO UNO dei due
import Login from "./components/Login"; // <--- usa questo
// import Login from "./Login";      // <--- se il tuo file è qui, scommenta questo e commenta quello sopra

// Feature reali
import Abbatimenti from "./features/abbattimenti/Abbatimenti";
import FrighiPanel from "./features/frighi/FrighiPanel";
import RicevimentoMerci from "./features/ricevimento/RicevimentoMerci";
import Sanificazione from "./features/sanificazione/Sanificazione";
import ProcedureListPage from "./features/procedure/ProcedureListPage";
import ProcedureViewer from "./features/procedure/ProcedureViewer";


// Modulo etichette (pagina con tab Stampa + Storico)
import EtichettePage from "./features/etichette/EtichettePage";

import Admin from "./features/admin/Admin";
import RequireAdmin from "./features/admin/RequireAdmin";
import ProfiloAzienda from "./features/admin/ProfiloAzienda";

// Parla con Norma (NEW)
import ChatNorma from "./features/norma/ChatNorma";

// Autoconfigurazione
import SetupWizard from "./features/setup/SetupWizard";

import CryoManualEntry from "./features/frighi/CryoManualEntry";
import CryoQrPrint from "./features/frighi/CryoQrPrint";

// Auth helpers
import { checkSession } from "./api_auth";
import { setUnauthorizedHandler } from "./http";

import ChecklistIgienePersonale from "./features/igiene/ChecklistIgienePersonale";
import RegistroAbbattimenti from "./features/abbattimenti/RegistroAbbattimenti";
import ManualePage from "./features/manuale/ManualePage";
import DichiarazioneSelvaggina from "./features/ricevimento/DichiarazioneSelvaggina";
import AuditHaccpPage from "./features/AuditHaccp/AuditHaccpPage";
import Infestanti from "./features/infestanti/Infestanti";
import Manutenzioni from "./features/manutenzioni/Manutenzioni";
import RichiediCorso from "./features/corsi/RichiediCorso";
import AllegatiGestionePage from "./features/allegati/AllegatiGestionePage";
import NonConformitaPage from "./features/nonconformita/NonConformitaPage";
import PanicModePage from "./features/panic/PanicModePage";
import InspectorAccessPage from "./features/panic/InspectorAccessPage";
import InspectorPortalPage from "./features/panic/InspectorPortalPage";
import InspectorSectionPrintPage from "./features/panic/InspectorSectionPrintPage";

import Termini from "./components/Termini";
import Privacy from "./components/Privacy";
import CookiePolicy from "./components/CookiePolicy";
import Security from "./components/Security";
import Footer from "./components/Footer";
import Registri from "./features/registri/Registri";
import ArchivioDdt from "./components/ArchivioDdt";
import LorawanDevicesPage from "./features/admin/LorawanDevicesPage";




/* ----------------------------------------
   Guard: login richiesto
---------------------------------------- */
function RequireAuth({ ready, autenticato, children }) {
  const location = useLocation();
  if (!ready) return <div style={{ padding: 16 }}>Verifica sessione…</div>;
  if (!autenticato) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

/* ----------------------------------------
   Guard: autoconfigurazione
---------------------------------------- */
function RequireSetup({ ready, needSetup, children }) {
  if (!ready) return <div style={{ padding: 16 }}>Verifica configurazione…</div>;
  if (needSetup) return <Navigate to="/setup" replace />;
  return children;
}

/* ----------------------------------------
   SetupRoute
---------------------------------------- */
function RequirePerm({ codes, children }) {
  const [state, setState] = useState({ loading: true, allowed: false });
  const requiredCodes = useMemo(() => (Array.isArray(codes) ? codes : [codes]), [codes]);

  useEffect(() => {
    let alive = true;
    fetch("/api/me.php", { credentials: "include" })
      .then((res) => res.json().catch(() => null))
      .then((me) => {
        if (!alive) return;
        const perms = Array.isArray(me?.permessi) ? me.permessi : [];
        const allowed =
          me?.ruolo === "superadmin" ||
          perms.includes("ALL") ||
          requiredCodes.some((code) => perms.includes(code));
        setState({ loading: false, allowed });
      })
      .catch(() => {
        if (alive) setState({ loading: false, allowed: false });
      });
    return () => {
      alive = false;
    };
  }, [requiredCodes]);

  if (state.loading) return <div style={{ padding: 16 }}>Verifica permessi...</div>;
  if (!state.allowed) return <Navigate to="/" replace />;
  return children;
}

function SetupRoute({ onDone }) {
  return (
    <SetupWizard
      onCompleted={() => {
        onDone?.();
        setTimeout(() => {
          window.location.href = "/";
        }, 200);
      }}
    />
  );
}

function InspectorOnlyGate({ ready, autenticato, inspectorOnly, children }) {
  const location = useLocation();

  if (!ready || !autenticato || !inspectorOnly) return children;

  const allowedPaths = [
    "/ispettore",
    "/ispettore-access",
    "/ispettore/stampa/",
    "/profilo-azienda",
    "/manuale",
    "/archivio-ddt",
    "/etichette",
    "/infestanti",
    "/procedure",
    "/procedure/",
    "/registri",
    "/non-conformita",
    "/privacy",
    "/termini",
    "/cookie-policy",
    "/security",
  ];
  const pathAllowed = allowedPaths.some((path) =>
    path.endsWith("/") ? location.pathname.startsWith(path) : location.pathname === path
  );
  if (pathAllowed) {
    return children;
  }

  return <Navigate to="/ispettore" replace />;
}

export default function App() {
  const [autenticato, setAutenticato] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [inspectorOnly, setInspectorOnly] = useState(false);

  const [needSetup, setNeedSetup] = useState(true);
  const [setupReady, setSetupReady] = useState(false);

  // interceptor 401
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAutenticato(false);
      setInspectorOnly(false);
      setNeedSetup(true);
      setSetupReady(true);
    });
  }, []);

  // bootstrap sessione
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const info = await checkSession();
        if (!alive) return;
        setAutenticato(!!info.authenticated);
        setNeedSetup(!!info.requires_setup);
        setInspectorOnly(!!info.inspector_only);
      } catch {
        if (!alive) return;
        setAutenticato(false);
        setInspectorOnly(false);
        setNeedSetup(true);
      } finally {
        if (alive) {
          setAuthReady(true);
          setSetupReady(true);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function doLogout() {
    try {
      await fetch("/api/logout.php", { method: "POST", credentials: "include" });
    } catch { }

    // ✅ reset stato frontend (QUESTO è il punto chiave)
    setAutenticato(false);
    setInspectorOnly(false);
    setNeedSetup(true);
    setSetupReady(true);
    setAuthReady(true);
  }

  // quando diventi autenticato, rilegge lo stato setup
  useEffect(() => {
    let alive = true;
    if (!autenticato) return;
    (async () => {
      try {
        setSetupReady(false);
        const info = await checkSession();
        if (!alive) return;
        setNeedSetup(!!info.requires_setup);
        setInspectorOnly(!!info.inspector_only);
      } catch {
        if (!alive) return;
        setNeedSetup(true);
        setInspectorOnly(false);
      } finally {
        if (alive) setSetupReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [autenticato]);

  return (
    <Router>
      <div className="min-h-screen flex flex-col">
        <div className="flex-1">
          <Suspense fallback={<div style={{ padding: 16 }}>Caricamento…</div>}>
            <InspectorOnlyGate ready={authReady} autenticato={autenticato} inspectorOnly={inspectorOnly}>
            <Routes>
              <Route path="/ispettore-access" element={<InspectorAccessPage />} />
              <Route
                path="/ispettore"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <InspectorPortalPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/ispettore/stampa/:sectionId"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <InspectorSectionPrintPage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />
              {/* LOGIN - una sola rotta */}
              <Route
                path="/login"
                element={
                  autenticato ? (
                    <Navigate to={inspectorOnly ? "/ispettore" : needSetup ? "/setup" : "/"} replace />
                  ) : (
                    <Login
                      onLogin={async () => {
                        setAutenticato(true);
                        try {
                          const info = await checkSession();
                          setNeedSetup(!!info.requires_setup);
                          setInspectorOnly(!!info.inspector_only);
                        } catch {
                          setNeedSetup(true);
                          setInspectorOnly(false);
                        }
                      }}
                    />
                  )
                }
              />

              {/* SETUP */}
              <Route
                path="/setup"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    {setupReady && !needSetup ? (
                      <Navigate to="/" replace />
                    ) : (
                      <SetupRoute onDone={() => setNeedSetup(false)} />
                    )}
                  </RequireAuth>
                }
              />

              {/* SETUP EDIT (riapre sempre configuratore) */}
              <Route
                path="/setup/edit"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    {/* non usare RequireSetup: deve essere accessibile anche se setup è completato */}
                    <SetupWizard
                      mode="edit"
                      onCompleted={() => {
                        // in edit non è necessario, ma non fa male
                        setNeedSetup(false);
                      }}
                    />
                  </RequireAuth>
                }
              />

              {/* DASHBOARD */}
              <Route
                path="/"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    {inspectorOnly ? (
                      <Navigate to="/ispettore" replace />
                    ) : (
                      <RequireSetup ready={setupReady} needSetup={needSetup}>
                        <Dashboard onLogout={doLogout} />
                      </RequireSetup>
                    )}
                  </RequireAuth>
                }
              />

              {/* NORMA */}
              <Route
                path="/norma"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RequirePerm codes="NORMA_AI">
                        <ChatNorma />
                      </RequirePerm>
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* PANIC MODE */}
              <Route
                path="/panic"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RequirePerm codes={["PANIC_REPORT", "PANIC_MODE"]}>
                        <PanicModePage />
                      </RequirePerm>
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* FRIGHI */}
              <Route
                path="/profilo-azienda"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ProfiloAzienda me={null} canEdit={false} />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              <Route
                path="/frighi"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <FrighiPanel />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* RICEVIMENTO MERCI */}
              <Route
                path="/ricevimento"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RicevimentoMerci />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* ALLEGATI DI GESTIONE */}
              <Route
                path="/allegati-gestione"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <AllegatiGestionePage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />


              {/* PROCEDURE - LISTA */}
              <Route
                path="/procedure"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ProcedureListPage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* PROCEDURE - VIEWER */}
              <Route
                path="/procedure/:slug"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ProcedureViewer />
                    </RequireSetup>
                  </RequireAuth>
                }
              />



              {/* DICHIARAZIONE SELVAGGINA */}
              <Route
                path="/selvaggina/:ricevimentoId"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <DichiarazioneSelvaggina />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* Richiedi corso */}
              <Route
                path="/richiedi-corso"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RichiediCorso />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* ETICHETTE (Stampa + Storico) */}
              <Route
                path="/etichette"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <EtichettePage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* ABBATTIMENTI */}
              <Route
                path="/abbattimenti"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <Abbatimenti />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              <Route
                path="/abbattimenti/registro"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <RegistroAbbattimenti />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* MANUTENZIONI */}
              <Route
                path="/manutenzioni"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <Manutenzioni />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* NON CONFORMITÀ (Allegato 4) */}
              <Route
                path="/non-conformita"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <NonConformitaPage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* SANIFICAZIONE */}
              <Route
                path="/sanificazione"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <Sanificazione />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* MANUALE HACCP */}
              <Route
                path="/manuale"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ManualePage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* AUDIT HACCP (Allegato 14) */}
              <Route
                path="/audit-haccp"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <AuditHaccpPage />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              {/* INFESTANTI (Monitoraggio interno) */}
              <Route
                path="/infestanti"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <Infestanti />
                    </RequireSetup>
                  </RequireAuth>
                }
              />


              {/* IGIENE PERSONALE */}
              <Route
                path="/igiene-personale"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <ChecklistIgienePersonale reparto="Cucina" />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              <Route
                path="/frighi/manual/:id"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <CryoManualEntry />
                    </RequireSetup>
                  </RequireAuth>
                }
              />

              <Route
                path="/frighi/qr/:id"
                element={
                  <RequireAuth ready={authReady} autenticato={autenticato}>
                    <RequireSetup ready={setupReady} needSetup={needSetup}>
                      <CryoQrPrint />
                    </RequireSetup>
                  </RequireAuth>
                }
              />


              {/* ADMIN → va messo PRIMA del catch-all */}
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <Admin />
                  </RequireAdmin>
                }
              />

              <Route path="/registri" element={<Registri />} />

              <Route path="/archivio-ddt" element={<ArchivioDdt />} />

              <Route
                path="/admin/lorawan-devices"
                element={
                  <RequireAdmin>
                    <LorawanDevicesPage />
                  </RequireAdmin>
                }
              />

              {/* INFO LEGALI */}
              <Route path="/termini" element={<Termini />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/security" element={<Security />} />

              {/* CATCH-ALL */}
              <Route
                path="*"
                element={
                  <Navigate
                    to={autenticato ? (inspectorOnly ? "/ispettore" : needSetup ? "/setup" : "/") : "/login"}
                    replace
                  />
                }
              />
            </Routes>
            </InspectorOnlyGate>
          </Suspense>
        </div>

        <Footer />
      </div>
    </Router>
  );
}
